/**
 * Resizable image NodeView for TipTap.
 * Shows drag handles when selected; persists width in the node attrs.
 */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { Loader2 } from 'lucide-react';
import {
    resolveMediaUrlForViewer,
    shouldResolveMediaUrlForViewer,
} from '@ai-content-hub/content-core';
import { cn } from '../../../lib/utils';
import { MediaLayoutFrame } from './MediaLayoutFrame';
import { DEFAULT_INSERT_IMAGE_MAX_WIDTH } from '../../../lib/tiptap-upload';

const MIN_WIDTH = 48;
const MAX_WIDTH_RATIO = 1; // fraction of container width

type ResizeDirection = 'left' | 'right';

export const ResizableImageView: React.FC<NodeViewProps> = (props) => {
    const { node, updateAttributes, deleteNode, selected } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [resizing, setResizing] = useState(false);

    const src = (node.attrs as { src?: string }).src ?? '';
    const alt = (node.attrs as { alt?: string }).alt ?? '';
    const title = (node.attrs as { title?: string }).title ?? undefined;
    const width = (node.attrs as { width?: number | null }).width ?? null;

    const [displaySrc, setDisplaySrc] = useState<string>(() =>
        src && !shouldResolveMediaUrlForViewer(src) ? src : '',
    );
    const [resolving, setResolving] = useState(() =>
        Boolean(src && shouldResolveMediaUrlForViewer(src)),
    );

    useEffect(() => {
        if (!src || src.startsWith('data:') || src.startsWith('blob:')) {
            setDisplaySrc(src);
            setResolving(false);
            return;
        }
        if (!shouldResolveMediaUrlForViewer(src)) {
            setDisplaySrc(src);
            setResolving(false);
            return;
        }
        let cancelled = false;
        setResolving(true);
        setDisplaySrc('');
        void (async () => {
            try {
                const resolved = await resolveMediaUrlForViewer(src);
                if (!cancelled) {
                    setDisplaySrc(resolved || src);
                }
            } catch {
                if (!cancelled) {
                    setDisplaySrc(src);
                }
            } finally {
                if (!cancelled) {
                    setResolving(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [src]);

    /* ------------------------------------------------------------------ */
    /* Resize via drag handles                                             */
    /* ------------------------------------------------------------------ */

    const handleMouseDown = useCallback(
        (direction: ResizeDirection, e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setResizing(true);

            const startX = e.clientX;
            const container = containerRef.current;
            if (!container) return;

            const startWidth =
                width ?? imgRef.current?.naturalWidth ?? imgRef.current?.offsetWidth ?? 300;
            const maxWidth = container.parentElement
                ? container.parentElement.clientWidth * MAX_WIDTH_RATIO
                : 9999;

            const onMouseMove = (ev: MouseEvent) => {
                const delta = direction === 'right' ? ev.clientX - startX : startX - ev.clientX;
                const next = Math.round(Math.max(MIN_WIDTH, Math.min(maxWidth, startWidth + delta)));
                updateAttributes({ width: next });
            };

            const onMouseUp = () => {
                setResizing(false);
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        },
        [width, updateAttributes]
    );

    /* Prevent cursor flicker while dragging */
    useEffect(() => {
        if (resizing) {
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }
        return () => {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [resizing]);

    if (!src) return null;

    const imageSrc = displaySrc || (resolving ? '' : src);
    if (!imageSrc && !resolving) return null;

    const showHandles = selected || resizing;

    return (
        <MediaLayoutFrame
            node={node}
            updateAttributes={updateAttributes}
            deleteNode={deleteNode}
            selected={selected}
            className={cn(
                'tiptap-resizable-image-wrapper',
                selected && 'tiptap-resizable-image--selected',
                resizing && 'tiptap-resizable-image--resizing'
            )}
        >
            <div
                ref={containerRef}
                className="tiptap-resizable-image relative inline-block"
                style={{ width: width ? `${width}px` : undefined, maxWidth: '100%' }}
            >
                <img
                    ref={imgRef}
                    src={imageSrc}
                    alt={alt}
                    title={title}
                    draggable={false}
                    className="tiptap-resizable-image-img block rounded-md"
                    // EAA-1736: with no explicit width (legacy content, failed
                    // measurement, or a dimensionless SVG) cap the display so the
                    // image cannot balloon to the full column; small images keep
                    // their natural size. An explicit width is honoured as-is.
                    style={{
                        width: width ? '100%' : undefined,
                        maxWidth: width ? '100%' : `min(100%, ${DEFAULT_INSERT_IMAGE_MAX_WIDTH}px)`,
                        height: 'auto',
                        opacity: resolving ? 0.5 : 1,
                    }}
                />
                {resolving && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                )}

                {/* Resize handles */}
                {showHandles && (
                    <>
                        <div
                            className="tiptap-resize-handle tiptap-resize-handle--left"
                            onMouseDown={(e) => handleMouseDown('left', e)}
                        />
                        <div
                            className="tiptap-resize-handle tiptap-resize-handle--right"
                            onMouseDown={(e) => handleMouseDown('right', e)}
                        />
                    </>
                )}

                {/* Width indicator while resizing */}
                {resizing && width && (
                    <span className="tiptap-resize-badge">
                        {width}px
                    </span>
                )}
            </div>
        </MediaLayoutFrame>
    );
};

ResizableImageView.displayName = 'ResizableImageView';
